/**
 * 장비 화면.
 *
 * 상점과 창고와 착장이 한 자리에 있다. 셋을 갈라 두면 "이 돈으로 무엇을 사서
 * 누구에게 줄 것인가"라는 하나의 결정이 화면 세 번 넘기는 일이 된다.
 *
 * 장수와 동료가 같은 목록을 쓴다. 그래서 왼쪽에서 누구를 꾸밀지 먼저 고르고,
 * 같은 창고를 그 사람에게 붙인다. 환두대도 한 자루를 장수에게 줄지 계백에게
 * 줄지가 이 화면의 질문이다.
 */
import {
  DROP_MIN_DEPTH,
  ENHANCE_MAX,
  enhanceMaterials,
  SLOT_NAMES,
  SLOT_ORDER,
  applyCommand,
  equipOf,
  heroPower,
  holdsBow,
  LOOT_PRICE_MULT,
  isMounted,
  shopPrice,
  shopTier,
  shoots,
  itemKey,
  loadoutIds,
  riderStats,
} from '../sim/equipment.ts';
import type { EquipDef, Loadout } from '../sim/equipment.ts';
import { CULTURE_NAMES, companionSpec, leaderSpec } from '../sim/troops.ts';
import type { CombatantSpec, Stats } from '../sim/troops.ts';
import { companionLevelOf, heroLevelOf, metCompanions } from '../meta/army.ts';
import type { Army } from '../meta/army.ts';
import {
  HERO,
  autoEquip,
  autoEquipAll,
  buy,
  canEnhanceWornBy,
  canWear,
  enhance,
  enhanceCheck,
  equipItem,
  loadoutOf,
  sell,
  sellPrice,
  shopFor,
  stashGroups,
  unequip,
} from '../meta/stash.ts';
import { button, clear, el, withKey } from './dom.ts';
import { closeItemTip, isLoot, itemIcon, itemTipButton, modSummary } from './items.ts';

interface Wearer {
  key: string;
  name: string;
  note: string;
  spec: CombatantSpec;
}

export class ArmoryScreen {
  private readonly root: HTMLElement;
  private readonly army: Army;
  private readonly onBack: () => void;
  private readonly onChange: () => void;
  private who = HERO;
  /**
   * 마지막으로 누른 단추의 열쇠(`data-key`). 다시 그린 뒤 그 단추를 같은 높이에 둔다.
   * 처음에는 화면을 통째로 지웠다 다시 그려, 사면 스크롤이 맨 위로 당겨지거나 창고에 한 줄이 늘어
   * 상점이 밀려 내려갔다 — 플레이한 사람이 "구매했을 때 스크롤이 변하지 않게" 달라고 했다.
   * 출정 화면(`SortieScreen.render`)과 같은 방법이다.
   */
  private anchorKey: string | null = null;
  /** 누른 단추가 다시 그린 뒤 옮겨 간 열쇠(강화하면 `w_x` → `w_x+1`). 없으면 누른 단추 그대로. */
  private anchorTarget: string | null = null;
  /** 앵커 단추가 다시 그린 뒤 없을 때(★3이라 강화 단추가 사라짐) 초점을 둘 곳. */
  private anchorFallback: string | null = null;
  /** 이번에 다시 그릴 때는 스크롤을 옮기지 않는다(강화). 초점은 예전처럼 돌려준다. */
  private anchorStay = false;
  /** 까닭 줄의 id 번호(`describe`). 그릴 때마다 이어 세어 한 화면 안에서 겹치지 않게 한다. */
  private noteSeq = 0;
  /** 자동 착용을 누른 뒤 한 번 보이는 한 줄(무엇을 바꿨는가). 다른 단추를 누르면 지운다. */
  private autoNote: string | null = null;
  /** '모두 가장 좋은 것으로'의 알림: 사람마다 한 줄. 한 번 그린 뒤 지운다. */
  private allNote: string[] | null = null;

  constructor(root: HTMLElement, army: Army, onBack: () => void, onChange: () => void, who?: string) {
    this.root = root;
    this.army = army;
    this.onBack = onBack;
    this.onChange = onChange;
    // 출정 화면의 장수·동료 상세에서 왔으면 그 사람을 골라 둔 채 연다. 모르는 이름이면 장수다.
    if (who !== undefined && this.wearers().some((w) => w.key === who)) this.who = who;
    // 브라우저의 스크롤 고정이 `render`의 제자리 맞추기 위에 또 밀지 않게(index.html overflow-anchor).
    root.classList.add('armory-screen');
    // click을 잡기 단계에서 본다 — 단추의 처리기(그 안에서 render가 돈다)보다 먼저다.
    root.addEventListener(
      'click',
      (e) => {
        this.anchorKey = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-key]')?.dataset.key ?? null;
      },
      true,
    );
  }

  private wearers(): Wearer[] {
    const out: Wearer[] = [
      // 레벨이 붙은 몸이다. 맨몸 기준(레벨 1)으로 재면 장비 값이 전장보다 크게 보인다.
      {
        key: HERO,
        name: '장수',
        note: `레벨 ${heroLevelOf(this.army)} · 직할대를 이끈다`,
        spec: leaderSpec(this.army.culture, heroLevelOf(this.army)),
      },
    ];
    // **만난 사람만 입힌다.** 문화의 동료 전부를 훑으면 아직 합류하지 않은
    // 사람에게 장비를 채울 수 있고, 그 장비는 창고에서 빠져나가 돌아오지 않는다.
    for (const c of metCompanions(this.army)) {
      const level = companionLevelOf(this.army, c.id);
      out.push({
        key: c.id,
        name: c.name,
        note: `${c.title} · Lv ${level}`,
        spec: companionSpec(c, level),
      });
    }
    return out;
  }

  private current(): Wearer {
    const list = this.wearers();
    return list.find((w) => w.key === this.who) ?? list[0];
  }

  private touch(): void {
    this.onChange();
    this.render();
  }

  /** 고른 사람에게 창고에서 가장 좋은 것을 입힌다(`autoEquip`). 바꾼 칸을 한 줄로 알린다. */
  private autoFit(): void {
    const w = this.current();
    const changed = autoEquip(this.army, w.key);
    const worn = loadoutOf(this.army, w.key);
    this.autoNote =
      changed.length === 0
        ? '창고에 지금보다 좋은 것이 없다.'
        : `바꿔 입혔다: ${changed.map((s) => equipOf(worn[s])?.name ?? SLOT_NAMES[s]).join(' · ')}`;
    this.onChange();
    this.render();
    this.autoNote = null;
  }

  /** 모두(장수와 동료들)에게 창고에서 가장 좋은 것을 입힌다(`autoEquipAll`). 누가 무엇을 바꿨는지 사람마다 한 줄. */
  private autoFitAll(): void {
    const people = this.wearers();
    const changed = autoEquipAll(this.army, people.map((w) => w.key));
    this.allNote =
      changed.length === 0
        ? ['창고에 지금보다 좋은 것이 없다.']
        : changed.map((c) => {
            const worn = loadoutOf(this.army, c.who);
            const name = people.find((w) => w.key === c.who)?.name ?? c.who;
            return `${name}: ${c.slots.map((s) => equipOf(worn[s])?.name ?? SLOT_NAMES[s]).join(' · ')}`;
          });
    this.onChange();
    this.render();
    this.allNote = null;
  }

  render(): void {
    const scroller = this.root.parentElement;
    const prevScroll = scroller?.scrollTop ?? 0;
    // 누른 단추(`key`)의 높이를 그리기 전에 재고, 다시 그린 뒤에는 그 단추가 간 곳(`target`)을 같은 높이에 둔다. 둘은
    // 다를 수 있다 — 창고의 ★1을 강화하면 ★2 묶음으로 합쳐진다. 처음에는 그리기 전 높이도 새 열쇠(이미 있던 ★2 단추)로
    // 재어 보정이 0이 되고 단추가 140px 위로 튀었다(리뷰가 짚었다).
    const key = this.anchorKey;
    const target = this.anchorTarget ?? key;
    this.anchorKey = null;
    this.anchorTarget = null;
    // 키보드로 누른 단추가 다시 그리며 사라지면 초점이 문서 밖(BODY)으로 빠진다. 누른 자리(같은 열쇠)로 돌려 준다.
    const hadFocus = this.root.contains(document.activeElement);
    const fallback = this.anchorFallback;
    this.anchorFallback = null;
    const stay = this.anchorStay;
    this.anchorStay = false;
    const find = (k: string | null): HTMLElement | null =>
      k ? this.root.querySelector<HTMLElement>(`[data-key="${CSS.escape(k)}"]`) : null;
    const before = find(key)?.getBoundingClientRect().top;
    // 다시 그리면 설명 창이 붙어 있던 줄이 사라진다. 창만 떠 있지 않게 먼저 닫는다.
    closeItemTip();
    clear(this.root);
    this.root.append(
      this.header(),
      el(
        'div',
        { class: 'armory-body' },
        this.people(),
        this.fitting(),
        el('div', { class: 'armory-col' }, ...this.stash(), this.shop()),
      ),
    );
    // 잠긴 단추(재료 없음)·없어진 단추(★3)는 초점을 받지 못한다 — 그때는 대신 갈 곳(그 물건의 설명 단추)으로.
    const own = find(target);
    const landed = own && !(own as HTMLButtonElement).disabled ? own : (find(fallback) ?? own);
    if (hadFocus) landed?.focus({ preventScroll: true });
    if (!scroller) return;
    // 지우는 순간 내용 높이가 0이 되어 브라우저가 스크롤을 당긴다. 되돌린 뒤, 누른 단추가 전과 같은
    // 높이에 오도록 한 번 더 민다(창고에 줄이 늘어 상점이 밀린 만큼).
    scroller.scrollTop = prevScroll;
    // 강화는 스크롤을 그 자리에 둔다(`anchorStay`) — 누른 단추를 쫓아가지 않는다. 강화한 창고 물건은 열쇠가 바뀌며
    // (★가 오른다) 좋은 것부터 서는 목록에서 제 티어의 위쪽으로 옮겨 가는데, 그 줄의 단추를 같은 높이에 두려고 밀면
    // 화면이 통째로 그 줄을 따라 튀었다(플레이한 사람이 짚었다: "장비를 강화하면 스크롤 위치가 달라져버리네. 유지
    // 해줄 수 있어?"). 사고·입히고·파는 것은 줄이 제자리에서 늘고 줄 뿐이라 예전처럼 단추를 같은 높이에 둔다.
    if (stay) return;
    const after = landed?.getBoundingClientRect().top;
    if (before !== undefined && after !== undefined) scroller.scrollTop += after - before;
  }

  private header(): HTMLElement {
    return el(
      'header',
      { class: 'sortie-head' },
      el(
        'div',
        { class: 'brand' },
        el('span', { class: 'eyebrow' }, `ILDANGBAEK 일당백 · ${CULTURE_NAMES[this.army.culture]}`),
        el('h1', {}, '장비'),
      ),
      el(
        'div',
        { class: 'ledger' },
        withGold(stat('군자금', `${this.army.gold}냥`)),
        stat('창고', `${this.army.stash.length}점`),
        button('출정 준비로', this.onBack, 'primary'),
      ),
    );
  }

  /** 누구를 꾸밀 것인가. 장수와 동료가 같은 창고를 나눠 쓴다. */
  private people(): HTMLElement {
    const rows = this.wearers().map((w) => {
      const worn = loadoutOf(this.army, w.key);
      const filled = SLOT_ORDER.filter((s) => worn[s]).length;
      const on = w.key === this.who;
      const b = button(
        '',
        () => {
          this.who = w.key;
          this.render();
        },
        `wearer${on ? ' on' : ''}`,
      );
      // 입은 것 가운데 강화할 수 있는 물건이 있으면 표(★) — 출정 화면의 장비 단추와 같은 표다(`canEnhanceWornBy`).
      // 표는 눈으로만 보이게 두고(`aria-hidden`) 뜻은 단추의 이름으로 읽어 준다.
      const up = canEnhanceWornBy(this.army, w.key);
      b.append(
        el('span', { class: 'wname' }, w.name, up ? el('small', { class: 'nav-badge', 'aria-hidden': 'true' }, '★') : null),
        el('span', { class: 'dim' }, w.note),
        el('span', { class: 'count' }, `${filled}/${SLOT_ORDER.length}`),
      );
      if (up) {
        b.setAttribute('aria-label', `${w.name} · ${w.note} · ${filled}/${SLOT_ORDER.length} — 강화할 수 있는 장비가 있다`);
        b.title = '강화할 수 있는 장비가 있다';
      }
      return b;
    });
    return el(
      'section',
      { class: 'panel people' },
      el(
        'div',
        { class: 'fit-head' },
        el('h2', {}, '누구에게'),
        // 장수와 동료 모두에게 한 번에(`autoEquipAll`). 아래 착장 칸의 단추는 고른 한 사람에게만이다. 장수 혼자면
        // 두 단추가 같은 일을 하므로 이쪽은 두지 않는다.
        rows.length > 1 ? withKey(button('모두 가장 좋은 것으로', () => this.autoFitAll(), 'mini'), 'auto-fit-all') : null,
      ),
      this.allNote
        ? el('div', { class: 'fit-note', role: 'status' }, ...this.allNote.map((line) => el('p', {}, line)))
        : null,
      ...rows,
    );
  }

  /**
   * 강화 단추(Todo10): 같은 물건(단계마다 두 배 — `enhanceMaterials`)과 냥으로 한 단계. **누를 수 있을 때만 단추다.** ★3이면 없다. `who`가 있으면 그
   * 사람이 입은 것, 없으면 창고의 것. 못 올리는 까닭은 단추가 아니라 물건 아래 한 줄로 적는다(`enhanceNote`).
   *
   * 처음에는 못 올릴 때도 까닭을 글자로 단 흐린 단추를 두었다(툴팁은 폰에서 읽을 수 없어서). 좁은 칸에서 그 단추가
   * 두 줄로 접히며 물건 이름을 밀어 티어 표와 겹쳤고, 누를 수 없는 것이 누를 것처럼 보였다(플레이한 사람이 짚었다).
   */
  private enhanceButton(key: string, who?: string): HTMLElement | null {
    const check = enhanceCheck(this.army, key, who === undefined);
    const item = equipOf(key);
    if (!check.ok || check.next > ENHANCE_MAX || check.next === 0 || !item) return null;
    // 재료가 둘 이상 드는 단계는 그 수를 적는다 — 누르면 창고의 같은 물건이 그만큼 사라진다.
    const mats = check.need ?? 1;
    const label = `★${check.next} 강화 ${check.cost.toLocaleString()}냥${mats > 1 ? ` · 재료 ${mats}` : ''}`;
    // 강화하면 열쇠가 바뀐다(`w_x` → `w_x+1`). 입은 것은 칸으로, 창고의 것은 새 열쇠로 다시 그린 뒤 같은 자리를 찾는다 —
    // 처음에는 옛 열쇠를 찾아 키보드 초점이 BODY로 빠졌다(리뷰가 짚었다). ★3이 되어 단추가 없어지면 그 물건의 설명 단추로.
    const up = itemKey(item.baseId, check.next);
    const b = withKey(
      button(
        label,
        () => {
          if (!enhance(this.army, key, who)) return;
          this.anchorTarget = who !== undefined ? `enh:${who}:${item.slot}` : `enh::${up}`;
          this.anchorFallback = who !== undefined ? `tip:${who}:${item.slot}` : `tip::${up}`;
          this.anchorStay = true;
          this.touch();
        },
        'mini',
      ),
      who !== undefined ? `enh:${who}:${item.slot}` : `enh::${key}`,
    );
    return b;
  }

  /**
   * 못 올리는 까닭 한 줄(`enhanceButton`이 없을 때). 재료가 없으면 무엇이 몇 개 더 있어야 하는지와 그때 들 값을, 냥이
   * 모자라면 얼마가 모자란지를 적는다. `material`이 거짓이면 재료 없음은 적지 않는다 — 창고의 물건마다 "하나 더"가
   * 붙으면 목록이 시끄럽다(창고에서 강화할 물건은 같은 것이 둘 있을 때만 뜻이 있다).
   */
  private enhanceNote(key: string, who: string | undefined, material: boolean): HTMLElement | null {
    const check = enhanceCheck(this.army, key, who === undefined);
    if (check.ok || check.next > ENHANCE_MAX || check.next === 0) return null;
    const star = el('b', {}, `★${check.next}`);
    if (check.lack === 'gold') {
      const short = check.cost - this.army.gold;
      return el('span', { class: 'enh-note' }, star, ` 강화 ${check.cost.toLocaleString()}냥 — ${short.toLocaleString()}냥 모자람`);
    }
    if (check.lack === 'material' && material) {
      // 모자란 수를 적는다(이 단계에 드는 수가 아니라) — 넷이 드는 ★3에 셋을 가졌으면 "1개 더"다.
      return el('span', { class: 'enh-note' }, star, ` 강화하려면 ${check.material} ${check.short ?? 1}개 더 · ${check.cost.toLocaleString()}냥`);
    }
    return null;
  }

  /**
   * 까닭 한 줄을 그 물건의 설명 단추에 묶는다(`aria-describedby`). 줄은 단추가 아니라 초점을 받지 않는다 — 키보드로 그
   * 물건에 오거나 강화 뒤 초점이 설명 단추로 돌아올 때 까닭도 함께 읽히게(리뷰가 짚었다).
   */
  private describe(target: Element | null, note: HTMLElement | null): void {
    if (!target || !note) return;
    note.id = `enh-note-${this.noteSeq++}`;
    target.setAttribute('aria-describedby', note.id);
  }

  /** 고른 사람의 다섯 칸. 군기는 본인이 아니라 분대에 걸린다. */
  private fitting(): HTMLElement {
    const w = this.current();
    const worn = loadoutOf(this.army, w.key);
    // 말이 몸을 바꾼다(걷다가 올라탄다). 장수도 동료도 전장과 같은 식(`riderStats`)으로
    // 잰다. 처음에는 장수만 그랬고 동료는 늘 말 위라 말의 올라탄 몫이 빠져 있었다.
    const bare = w.spec.stats;
    const dressed = riderStats(bare, worn);
    const onFoot = !isMounted(worn);
    const base = { atkPct: 100, defBonus: 0, gritBonus: 0 };
    const cmd = applyCommand(base, loadoutIds(worn));

    const rows = SLOT_ORDER.map((slot) => {
      const id = worn[slot];
      const item = id ? equipOf(id) : undefined;
      const tip = item ? withKey(itemTipButton(item, 32, item.name), `tip:${w.key}:${slot}`) : null;
      const note = item ? this.enhanceNote(item.id, w.key, true) : null;
      this.describe(tip, note);
      return el(
        'div',
        { class: `slot-row${item ? ' filled' : ''}` },
        el('span', { class: 'k' }, SLOT_NAMES[slot]),
        tip ?? el('span', { class: 'v' }, '비어 있음'),
        // 창고 줄과 같은 티어 표(T1~T5). 착장에만 없어 무엇이 좋은 것인지 창을 열어야 알았다(플레이한 사람: "착장에도
        // 티어를 표시해줘").
        item ? el('span', { class: `tier t${item.tier}` }, `T${item.tier}`) : null,
        item
          ? el(
              'span',
              { class: 'slot-acts' },
              this.enhanceButton(item.id, w.key),
              button(
                '벗기기',
                () => {
                  unequip(this.army, w.key, slot);
                  this.touch();
                },
                'mini',
              ),
            )
          : el('span', { class: 'dim' }, '—'),
        note,
      );
    });

    const gain = Math.round(((heroPower(dressed) - heroPower(bare)) * 100) / heroPower(bare));
    const cmdParts: string[] = [];
    if (cmd.atkPct !== 100) cmdParts.push(`공격 +${cmd.atkPct - 100}%`);
    if (cmd.defBonus > 0) cmdParts.push(`방어 +${cmd.defBonus}`);
    if (cmd.gritBonus > 0) cmdParts.push(`사기 +${cmd.gritBonus}`);

    return el(
      'section',
      { class: 'panel fitting' },
      el(
        'div',
        { class: 'fit-head' },
        el('h2', {}, `${w.name}의 착장`),
        // 창고(아무도 입지 않은 것)에서 칸마다 가장 좋은 것을. 다른 사람이 입은 것은 건드리지 않는다.
        withKey(button('가장 좋은 것으로', () => this.autoFit(), 'mini'), 'auto-fit'),
      ),
      this.autoNote ? el('p', { class: 'fit-note', role: 'status' }, this.autoNote) : null,
      el(
        'div',
        { class: 'fit-power' },
        el('span', { class: 'big-num' }, String(heroPower(dressed))),
        // 활을 들면 지표가 맨몸보다 낮게 나온다(간격이 길고, 멀리서 치는 몫은 지표에 없다). 처음에는 음수를 생각하지 않아
        // "맨몸 대비 +-22%"로 적혔다.
        // 줄을 바꾸지 않는다 — 옆에 경고 글이 늘면서 360 폭에서 "맨몸 대 / 비 / +17%" 세 줄로 접혔다(QA).
        el('span', { class: 'dim', style: 'white-space: nowrap' }, gain === 0 ? '맨몸' : `맨몸 대비 ${gain > 0 ? '+' : '−'}${Math.abs(gain)}%`),
        shoots(worn) && el('span', { class: 'dim' }, '활 — 멀리서 치는 몫은 전투력에 들지 않는다'),
        // 말 위의 활은 쓰지 않는다(`fieldLoadout`). 여기 적지 않으면 활을 들렸는데 왜 숫자가 그대로인지 알 수 없다.
        holdsBow(worn) && !shoots(worn) && el('span', { class: 'warn' }, '말 위에서는 활을 쏘지 못한다 — 말을 벗겨야 쏜다(지금은 맨손 칼로 싸운다)'),
        onFoot && el('span', { class: 'warn' }, shoots(worn) ? '말이 없어 걷는다 — 걸어야 활을 쏜다' : '말이 없어 걷는다 — 느리지만 창에 약하지 않다'),
      ),
      ...rows,
      el(
        'div',
        { class: 'cmd-box' },
        el('b', {}, '휘하 분대에 걸리는 보정'),
        el('span', {}, cmdParts.length > 0 ? cmdParts.join(' · ') : '없음'),
      ),
      el('p', { class: 'dim' }, '전멸하면 차고 있던 것이 칸마다 35% 확률로 사라진다.'),
      // 강화가 있다는 것을 적어 둔다 — 단추는 같은 물건이 둘일 때만 살아나 그 전에는 눈에 띄지 않는다.
      el(
        'p',
        { class: 'dim' },
        `같은 물건(★ 없는 것)을 재료로 냥을 내고 강화한다 — ${Array.from({ length: ENHANCE_MAX }, (_, k) => `★${k + 1}에 ${enhanceMaterials(k + 1)}개`).join(', ')}(실패 없음).`,
      ),
    );
  }

  private stash(): HTMLElement[] {
    const all = stashGroups(this.army);
    const w = this.current();
    const row = (g: (typeof all)[number]): HTMLElement => {
      const wearable = canWear(this.army.culture, g.item);
      const name = itemName(g.item, g.count, wearable ? w : undefined, loadoutOf(this.army, w.key));
      // 전리품(상점에서 살 수 없는 물건)은 줄에 딱지를 단다 — 팔면 다시 살 수 없다. 시즌 보상도 여기 든다(`isLoot`)
      // — 어디서 왔는지는 물건의 설명이 가르고, 딱지의 풍선말은 공통인 것만 적는다(리뷰가 짚었다).
      const looted = isLoot(g.item);
      // 딱지는 티어 곁에 둔다 — 줄 끝에 두면 긴 설명 뒤로 밀려 줄마다 자리가 달랐다.
      if (looted) {
        const tag = el('span', { class: 'loot-tag', title: '상점에서 살 수 없는 물건' }, '전리품');
        const tier = name.querySelector('.tier');
        if (tier) tier.after(tag);
        else name.append(tag);
      }
      const note = this.enhanceNote(g.item.id, undefined, false);
      this.describe(name.matches('button') ? name : name.querySelector('button'), note);
      return el(
        'div',
        { class: `item-row${looted ? ' looted' : ''}`, 'data-item': g.item.id },
        name,
        el(
          'div',
          { class: 'item-actions' },
          wearable
            ? button(
                `${w.name}에게`,
                () => {
                  equipItem(this.army, w.key, g.item.id);
                  this.touch();
                },
                'mini',
              )
            : el('span', { class: 'dim' }, `${CULTURE_NAMES[g.item.culture!]} 전용`),
          this.enhanceButton(g.item.id),
          button(
            `팔기 ${sellPrice(g.item)}냥`,
            () => {
              sell(this.army, g.item.id);
              this.touch();
            },
            'mini',
          ),
        ),
        note,
      );
    };

    // **한 칸에, 좋은 것부터.** 전리품(깊은 곳에서만 나오는 티어 3~5, 살 수 없는 물건)은 한때 따로 위에 금빛 칸으로
    // 두었다(같은 사람이 청했다: "전리품 아이템 섹션은 별도로 만들어서 눈에 잘 띄게하자" — 상점 물건과 섞이면 묻혔다).
    // 그 뒤 창고가 좋은 것부터 서게 되어(`stashGroups`) 전리품은 저절로 위에 온다 — 칸 둘이 같은 단추의 같은 목록이라
    // 합쳤다("전리품과 창고를 합칠까"). 따로 둘 때 읽히던 것은 줄에 남긴다: 전리품 딱지와 금빛 테두리(팔면 다시 살 수
    // 없는 물건), 제목 옆의 전리품 수, 비었을 때의 "어디서 얻나".
    const lootCount = all.reduce((n, g) => n + (isLoot(g.item) ? g.count : 0), 0);
    return [
      el(
        'section',
        { class: 'panel stash' },
        el(
          'h2',
          {},
          '창고',
          el('span', { class: 'count' }, `${this.army.stash.length}점`),
          lootCount > 0 ? el('span', { class: 'count loot-count' }, `전리품 ${lootCount}점`) : null,
        ),
        all.length > 0
          ? el('div', { class: 'item-list' }, ...all.map(row))
          : el('p', { class: 'dim' }, `비어 있다. 상점에서 사거나, 깊이 ${DROP_MIN_DEPTH}부터 전장을 쓸면 전리품을 주울 수 있다.`),
        // 전리품이 아직 없으면 어디서 얻는지 한 줄 — 예전 전리품 칸의 빈 글이 하던 일이다.
        all.length > 0 && lootCount === 0
          ? el('p', { class: 'dim stash-hint' }, `전리품은 아직 없다. 깊이 ${DROP_MIN_DEPTH}부터 전장을 쓸면 주울 수 있다.`)
          : null,
      ),
    ];
  }

  /**
   * 산 순간의 손맛. 플레이한 사람이 짚었다: "상점에서 아이템 구매 버튼을 눌렀을 때 아무일도 안일어난 느낌이야. 돈이
   * 지불되고 아이템이 생겼다는 느낌이 확오게." 예전에는 다시 그리기만 해서 숫자 둘이 조용히 바뀌었다.
   * - 단추 자리에서 "−120냥"이 떠오르며 옅어지고, 군자금이 옛 값에서 새 값으로 줄어든다(깜빡임과 함께).
   * - 산 물건의 아이콘이 상점 줄에서 창고의 그 줄로 날아가 앉고, 그 줄이 잠깐 빛난다.
   * - 아래에 "○○을 샀다 · 창고로" 한 줄.
   * 다시 그린 뒤에 부른다 — 새 창고 줄과 군자금 칸이 있어야 한다. 움직임을 줄이는 설정이면 날지 않고 빛나기만 한다.
   */
  private buyFx(item: EquipDef, from: DOMRect | null, at: DOMRect, goldBefore: number): void {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 돈이 나간다.
    // 효과는 이 화면(`root`) 안에 붙인다 — 화면을 떠나면(다시 그리거나 출정 화면으로) 함께 지워진다. 처음에는 body에
    // 붙여, 사자마자 "출정 준비로"를 누르면 알림이 출정 화면의 단추를 덮어 누르기를 가로챘다(리뷰가 짚었다).
    const host = this.root;
    floatText(host, `−${shopPrice(item).toLocaleString()}냥`, at.left + at.width / 2, at.top, still);
    const gold = this.root.querySelector<HTMLElement>('[data-gold] .v');
    if (gold) countDown(gold, goldBefore, this.army.gold, still);
    // 물건이 들어온다.
    const row = this.root.querySelector<HTMLElement>(`.item-row[data-item="${CSS.escape(item.id)}"]`);
    const glow = (): void => {
      if (!row) return;
      row.classList.remove('just-bought');
      void row.offsetWidth; // 같은 줄을 연달아 사면 빛을 처음부터 다시.
      row.classList.add('just-bought');
    };
    const to = row?.querySelector('.item-icon')?.getBoundingClientRect() ?? null;
    if (from && to && !still) flyIcon(host, item, from, to, glow);
    else glow();
    toast(host, `${item.name}${josaObj(item.name)} 샀다 · 창고로`);
  }

  private shop(): HTMLElement {
    const w = this.current();
    const worn = loadoutOf(this.army, w.key);
    const rows = shopFor(this.army).map((item) => {
      // 전리품 티어는 값어치의 두 배다(`shopPrice`).
      const price = shopPrice(item);
      const afford = this.army.gold >= price;
      const line = el(
        'div',
        { class: 'item-row' },
        itemName(item, 1, canWear(this.army.culture, item) ? w : undefined, worn),
      );
      const buyBtn = withKey(
        button(
          `${price.toLocaleString()}냥`,
          () => {
            // 산 뒤에 화면을 다시 그리므로 날아갈 자리(아이콘·단추)는 그리기 전에 잰다.
            const from = line.querySelector('.item-icon')?.getBoundingClientRect() ?? null;
            const at = buyBtn.getBoundingClientRect();
            const goldBefore = this.army.gold;
            if (!buy(this.army, item.id)) {
              refuse(this.root, buyBtn, `${(price - this.army.gold).toLocaleString()}냥 모자란다`);
              return;
            }
            this.touch();
            this.buyFx(item, from, at, goldBefore);
          },
          `mini${afford ? '' : ' off'}`,
        ),
        `buy:${item.id}`,
      );
      line.append(el('div', { class: 'item-actions' }, buyBtn));
      return { tier: item.tier, price, line };
    });
    // 티어마다 머리줄을 둔다(값은 티어마다 같다). 전리품 티어까지 팔게 되며 줄이 서른을 넘어, 폰에서 여섯 화면을 내리며
    // 배지의 색으로만 티어를 갈라야 했다(QA).
    const listed: HTMLElement[] = [];
    let seen = 0;
    for (const r of rows) {
      if (r.tier !== seen) {
        seen = r.tier;
        listed.push(el('h3', { class: 'shop-tier' }, `${r.tier}티어`, el('span', { class: 'dim' }, ` ${r.price.toLocaleString()}냥`)));
      }
      listed.push(r.line);
    }

    return el(
      'section',
      { class: 'panel shop' },
      el('h2', {}, '상점'),
      el('p', { class: 'dim' }, shopNote(this.army.deepest)),
      el('div', { class: 'item-list' }, ...listed),
    );
  }
}

/**
 * 물건 한 줄. 누가 차면 얼마나 세지는지(`gain`)를 함께 적는다.
 *
 * 처음에는 보정 목록만 적었다. "철모와 환두대도가 값이 같은데 성능이 비슷한가"를 물으면 공격 +6·간격 +2와
 * 공격 +4·간격 −1을 머릿속에서 견줘야 했다. 지금 고른 사람의 몸으로 잰 전투력 차이를 한 숫자로 적는다.
 */
function itemName(item: EquipDef, count = 1, wearer?: Wearer, worn?: Loadout): HTMLElement {
  return el(
    'div',
    { class: 'item-main' },
    // 강화 뒤 초점이 돌아올 자리(`enhanceButton`). 창고 줄이 먼저 그려지므로 같은 물건의 상점 줄보다 앞에서 찾힌다.
    withKey(itemTipButton(item, 32, item.name), `tip::${item.id}`),
    el('span', { class: `tier t${item.tier}` }, `T${item.tier}`),
    el('span', { class: 'dim' }, SLOT_NAMES[item.slot]),
    // 공격 속도 %는 옆의 전투력 %와 같은 비교(지금 착장 → 이것으로 바꿈)로 잰다. 처음에는 맨몸 간격을
    // 기준으로 해, 환도대도를 찬 채 철모를 보면 −11%로 적었는데 실제 교체는 −17%였다(리뷰가 짚었다).
    el('span', { class: 'dim' }, modSummary(item, wearer && worn ? swapStats(item, wearer, worn) : undefined)),
    // 까닭을 화면에 바로 적는다. 처음에는 툴팁에만 두어 터치·키보드로는 읽을 수 없었다(리뷰가 짚었다).
    item.metricBlind && wearer ? el('span', { class: 'blind' }, `※ ${item.metricBlind}`) : null,
    wearer && worn ? gainTag(item, wearer, worn) : null,
    count > 1 ? el('span', { class: 'count' }, `×${count}`) : null,
  );
}

/** 이 사람이 지금 착장에서 이 물건으로 바꿔 차면 전투력이 몇 % 바뀌나. 휘하 보정은 따로다. */
function gainTag(item: EquipDef, w: Wearer, worn: Loadout): HTMLElement {
  const now = heroPower(riderStats(w.spec.stats, worn));
  const next = heroPower(riderStats(w.spec.stats, { ...worn, [item.slot]: item.id }));
  const pct = Math.round(((next - now) * 100) / now);
  const same = worn[item.slot] === item.id;
  return el(
    'span',
    { class: `gain${pct > 0 ? ' up' : pct < 0 ? ' down' : ''}`, title: `${w.name}의 지금 착장에서 ${SLOT_NAMES[item.slot]}을(를) 이것으로 바꾸면` },
    same
      ? '차고 있음'
      : pct === 0 && item.command
        ? '몸은 그대로 · 분대 보정'
        : `${w.name} ${pct > 0 ? '+' : ''}${pct}%`,
  );
}

/** 지금 착장의 몸과, 이 물건으로 바꿔 찼을 때의 몸. */
function swapStats(item: EquipDef, w: Wearer, worn: Loadout): [Stats, Stats] {
  return [riderStats(w.spec.stats, worn), riderStats(w.spec.stats, { ...worn, [item.slot]: item.id })];
}


/** 군자금 칸에 표를 단다(`buyFx`가 찾는다). */
function withGold(node: HTMLElement): HTMLElement {
  node.dataset.gold = '';
  return node;
}

/**
 * 상점의 안내: 지금 몇 티어까지 파는지, 다음 티어는 언제 열리는지, 전리품 티어의 값이 왜 비싼지. 2티어까지는 늘 팔고,
 * 그 위는 얻을 수 있는 티어보다 하나 아래까지 판다(`shopTier`).
 */
export function shopNote(deepest: number): string {
  const top = shopTier(deepest);
  // 다음 티어가 열리는 깊이: 그보다 한 티어 위가 떨어지기 시작하는 깊이다.
  let next = 0;
  for (let d = Math.max(1, deepest + 1); d <= 60 && next === 0; d++) if (shopTier(d) > top) next = d;
  const now = top <= 2 ? '여기서 파는 것은 2티어까지다.' : `여기서 파는 것은 ${top}티어까지다 — 이긴 가장 깊은 곳에서 얻을 수 있는 것보다 한 티어 아래까지.`;
  const later = next > 0 ? ` 깊이 ${next}에서 이기면 ${top + 1}티어도 판다.` : ' 5티어(무기·투구·군기의 가장 좋은 것)는 깊은 곳에서만 나온다.';
  const price = top >= 3 ? ` 3티어부터는 값어치의 ${LOOT_PRICE_MULT}배다(주우면 공짜인 물건이다).` : '';
  return now + later + price;
}

/** 받침에 따라 을/를. */
function josaObj(word: string): string {
  const c = word.charCodeAt(word.length - 1);
  return c >= 0xac00 && c <= 0xd7a3 && (c - 0xac00) % 28 !== 0 ? '을' : '를';
}

/** 화면 한 점에서 글이 떠오르며 옅어진다(돈이 나가는 자리). */
function floatText(host: HTMLElement, text: string, x: number, y: number, still: boolean): void {
  const n = el('div', { class: 'buy-float', 'aria-hidden': 'true' }, text);
  n.style.left = `${x}px`;
  n.style.top = `${y}px`;
  host.append(n);
  const a = n.animate(
    still
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { transform: 'translate(-50%, 0)', opacity: 1 },
          { transform: 'translate(-50%, -40px)', opacity: 0 },
        ],
    { duration: 900, easing: 'ease-out' },
  );
  a.onfinish = () => n.remove();
}

/** 숫자가 옛 값에서 새 값으로 줄어든다. 칸은 잠깐 붉게 깜빡인다. */
function countDown(node: HTMLElement, from: number, to: number, still: boolean): void {
  const cell = node.parentElement;
  cell?.classList.remove('spent');
  void cell?.offsetWidth;
  cell?.classList.add('spent');
  if (still) return;
  const t0 = performance.now();
  const step = (): void => {
    const t = Math.min(1, (performance.now() - t0) / 450);
    node.textContent = `${Math.round(from + (to - from) * t).toLocaleString()}냥`;
    if (t < 1 && node.isConnected) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** 아이콘이 상점 줄에서 창고 줄로 포물선을 그리며 날아가 앉는다. 앉으면 `done`. */
function flyIcon(host: HTMLElement, item: EquipDef, from: DOMRect, to: DOMRect, done: () => void): void {
  const icon = itemIcon(item, 32);
  icon.classList.add('buy-fly');
  icon.style.left = `${from.left}px`;
  icon.style.top = `${from.top}px`;
  host.append(icon);
  const dx = to.left - from.left;
  const dy = to.top - from.top;
  const a = icon.animate(
    [
      { transform: 'translate(0, 0) scale(1)', offset: 0 },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 60}px) scale(1.5)`, offset: 0.45 },
      { transform: `translate(${dx}px, ${dy}px) scale(1)`, offset: 1 },
    ],
    { duration: 650, easing: 'cubic-bezier(0.3, 0.6, 0.4, 1)' },
  );
  a.onfinish = () => {
    icon.remove();
    // 날아가는 동안 화면을 떠났으면 빛낼 줄도 없다.
    if (host.isConnected) done();
  };
}

/** 아래 가운데 한 줄 알림. 새 알림이 오면 앞의 것을 바꾼다. */
function toast(host: HTMLElement, text: string): void {
  host.querySelector('.buy-toast')?.remove();
  const n = el('div', { class: 'buy-toast', role: 'status' }, text);
  host.append(n);
  setTimeout(() => n.remove(), 1800);
}

/** 살 수 없을 때: 단추가 흔들리고 까닭을 한 줄. */
function refuse(host: HTMLElement, btn: HTMLElement, why: string): void {
  btn.classList.remove('shake');
  void btn.offsetWidth;
  btn.classList.add('shake');
  toast(host, why);
}

function stat(label: string, value: string): HTMLElement {
  return el(
    'div',
    { class: 'stat' },
    el('span', { class: 'k' }, label),
    el('span', { class: 'v' }, value),
  );
}

